import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useUserStore from '../store/userStore';
import useUserFeaturesStore from '../store/userFeaturesStore';
import authService from '../services/auth.service';

/**
 * RequireSubscription Component
 * Kiểm tra subscription của user đã đăng nhập
 * - Nếu chưa login -> redirect /login
 * - Nếu đã login nhưng không có subscription active -> redirect /compare-plans
 * - Nếu có subscription active -> render children
 *
 * Ưu tiên đọc subscription từ userStore (được hydrate từ login response)
 * để tránh race condition với userFeaturesStore.fetchUserFeatures
 */
const RequireSubscription = ({ children, fallbackPath = '/compare-plans' }) => {
  const location = useLocation();
  const user = useUserStore((state) => state.user);
  const userLoading = useUserStore((state) => state.loading);
  const isLoggedIn = authService.isLoggedIn();

  const { initialized, loading: featuresLoading, fetchUserFeatures } = useUserFeaturesStore();

  // Fetch features chỉ để lấy permissions/limits, không cần cho subscription check
  useEffect(() => {
    if (isLoggedIn && !initialized && !featuresLoading) {
      fetchUserFeatures();
    }
  }, [isLoggedIn, initialized, featuresLoading, fetchUserFeatures]);

  // Chưa login -> redirect /login
  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Khi user đã login nhưng chưa có user data (F5/refresh):
  // - Nếu đang loading: hiển thị loading spinner
  // - Nếu chưa loading: có thể fetchUser chưa kịp set loading=true, vẫn hiển thị loading
  // Chỉ redirect khi CHẮC CHẮN user không có subscription
  const hasUserData = user !== null;
  const isLoading = userLoading || !hasUserData;

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-screen tw-bg-gray-50 dark:tw-bg-fintech-navy">
        <div className="tw-text-center">
          <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-500 tw-mx-auto" />
          <p className="tw-mt-4 tw-text-gray-600 dark:tw-text-gray-400">Đang tải thông tin...</p>
        </div>
      </div>
    );
  }

  // Lấy subscription từ userStore (hydrate từ login/getme)
  const subscription = user?.subscription;

  // Kiểm tra subscription có active không (status === 1)
  const hasActiveSubscription = subscription && subscription.status === 1;

  // Không có subscription active -> redirect trực tiếp sang /compare-plans
  if (!hasActiveSubscription) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Có subscription active -> render children
  return children;
};

export default RequireSubscription;
